import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  House, BookOpenText, ListChecks, GraduationCap, ChatCircleDots,
  ClipboardText, Question, SignOut, Compass, Crown,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/app", label: "Overview", icon: House, testId: "nav-overview" },
  { to: "/app/frameworks", label: "Frameworks", icon: BookOpenText, testId: "nav-frameworks" },
  { to: "/app/assessment", label: "3rd-Party Assessment", icon: ListChecks, testId: "nav-assessment" },
  { to: "/app/interview", label: "Interview Q&A", icon: Question, testId: "nav-interview" },
  { to: "/app/quizzes", label: "Quizzes", icon: GraduationCap, testId: "nav-quizzes" },
  { to: "/app/tutor", label: "AI Tutor", icon: ChatCircleDots, testId: "nav-tutor" },
  { to: "/app/checklists", label: "Checklists", icon: ClipboardText, testId: "nav-checklists" },
  { to: "/app/pricing", label: "Upgrade to Pro", icon: Crown, testId: "nav-pricing" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-[260px_1fr] bg-background">
      <aside className="hidden md:flex flex-col border-r border-border bg-white sticky top-0 h-screen">
        <div className="px-6 py-6 border-b border-border">
          <Link to="/app" className="flex items-center gap-2" data-testid="brand-link">
            <div className="w-8 h-8 bg-primary text-primary-foreground flex items-center justify-center">
              <Compass size={18} weight="bold" />
            </div>
            <div className="font-display text-lg leading-none">DKK<br/>Info Tech</div>
          </Link>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/app"}
              data-testid={n.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm border-l-2 transition-colors ${
                  isActive
                    ? "border-primary bg-secondary text-foreground font-medium"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                }`
              }
            >
              <n.icon size={18} weight="regular" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-border">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-muted-foreground truncate flex-1" data-testid="sidebar-user-email">{user?.email}</div>
            {user?.is_pro && (
              <span className="text-[10px] font-mono uppercase tracking-widest bg-primary text-primary-foreground px-1.5 py-0.5" data-testid="sidebar-pro-badge">Pro</span>
            )}
          </div>
          <button
            data-testid="logout-button"
            onClick={() => { logout(); nav("/"); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm border border-border hover:bg-secondary transition-colors"
          >
            <SignOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-border bg-white sticky top-0 z-10">
        <Link to="/app" className="font-display text-lg" data-testid="brand-link-mobile">DKK Info Tech</Link>
        <button data-testid="logout-button-mobile" onClick={() => { logout(); nav("/"); }} className="text-xs px-3 py-1 border border-border">Sign out</button>
      </div>

      <main className="min-w-0">
        {/* Mobile nav pills */}
        <div className="md:hidden overflow-x-auto border-b border-border bg-white">
          <div className="flex px-4 py-2 gap-2 whitespace-nowrap">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/app"}
                className={({ isActive }) => `text-xs px-3 py-1 border ${isActive ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}
                data-testid={`${n.testId}-mobile`}
              >{n.label}</NavLink>
            ))}
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
          {children}
        </div>
      </main>
    </div>
  );
}
